import React, { forwardRef, useEffect } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  Divider,
  Header,
  Segment,
  Table,
  Icon,
  Input,
  Popup,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import _ from "lodash";
import { Calendar as RBCalendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { format, getDay, parse, startOfWeek } from "date-fns";
import {
  addTasksFromDocToDb,
  getFormTasksFromDb,
  updateTransFieldsInDb,
} from "../../../app/firestore/firestoreService";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { functionGetDeadlinesFromPdf } from "../../../app/firestore/functionsService";
import {
  convertFullName,
  convertPartiesAbleToShare,
  convertRoleToSharingDisplay,
  extractPdfDeadlinesFromTable,
  partyIsAgent,
} from "../../../app/common/util/util";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { parseDateSafe } from "../../../app/common/util/dateUtils";

const DnDCalendar = withDragAndDrop(RBCalendar);

function formatCurrencyDisplay(value) {
  if (value === null || value === undefined || value === "") return null;
  const str = String(value).trim();
  if (!str) return null;
  return str.startsWith("$") ? str : `$${str}`;
}

function stripCurrency(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\$/g, "").trim();
}

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function DocExtractDeadlines({ doc }) {
  const dispatch = useDispatch();
  const [tasks, setTasks] = useState();
  const [contractInfo, setContractInfo] = useState(null); // extra fields from Gemini
  const { tasksTransWithDateAll } = useSelector((state) => state.task);
  const { transaction, allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [extractingDeadlines, setExtractingDeadlines] = useState(true);
  const [viewMode, setViewMode] = useState("table");
  const [selectedPartyIds, setSelectedPartyIds] = useState(new Set());
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const partiesAvailToShare = React.useMemo(
    () =>
      convertPartiesAbleToShare(allParties || [], transaction.agentRepresents).filter(
        (p) => p.email?.trim()
      ),
    [allParties, transaction.agentRepresents]
  );

  function handlePartyCheckboxChange(partyId, checked) {
    const next = new Set(selectedPartyIds);
    checked ? next.add(partyId) : next.delete(partyId);
    setSelectedPartyIds(next);
  }

  useEffect(() => {
    async function extractDeadlinesFromUploadedPdf() {
      const result = await functionGetDeadlinesFromPdf(doc.docRef);

      // Support both the legacy array format and the new { rows, geminiData } shape
      const rows = Array.isArray(result) ? result : (result?.rows || []);
      const geminiData = Array.isArray(result) ? null : (result?.geminiData || null);

      let newTasks = [];

      if (geminiData?.deadlines?.length > 0) {
        // Image-only PDF: use Gemini-extracted deadlines
        geminiData.deadlines.forEach((deadline) => {
          const timestamp = parseDateSafe(deadline.date);
          if (timestamp) {
            newTasks.push({
              uniqueId: deadline.name,
              end: timestamp,
              title: deadline.name,
              assignedTo: [],
              docId: doc.id,
              docTitle: doc.title,
              category: "Deadline",
              transactionId: transaction.id,
              transactionTitle: transaction.title,
              status: "Active",
              active: true,
            });
          }
        });

        // Surface the contract-level fields for display
        const info = {};
        if (geminiData.contractDate) {
          const dateObj = parseDateSafe(geminiData.contractDate);
          if (dateObj) info.contractDate = dateObj;
        }
        if (geminiData.salesPrice) info.salesPrice = geminiData.salesPrice;
        if (geminiData.earnestMoney) info.earnestMoney = geminiData.earnestMoney;
        if (geminiData.propertyAddress) info.propertyAddress = geminiData.propertyAddress;
        if (Object.keys(info).length > 0) setContractInfo(info);
      } else {
        // Text-based PDF: use existing pdf2json text extraction
        const deadlines = extractPdfDeadlinesFromTable(rows);
        Object.entries(deadlines).forEach(([key, value]) => {
          const timestamp = parseDateSafe(value);
          if (timestamp) {
            newTasks.push({
              uniqueId: key,
              end: timestamp,
              title: key,
              assignedTo: [],
              docId: doc.id,
              docTitle: doc.title,
              category: "Deadline",
              transactionId: transaction.id,
              transactionTitle: transaction.title,
              status: "Active",
              active: true,
            });
          }
        });
      }

      newTasks = _.sortBy(newTasks, "end");
      newTasks.forEach((task, index) => {
        task.uniqueId = index;
      });
      setTasks(newTasks);
      setExtractingDeadlines(false);
    }
    function extractDeadlinesFromUploadedForm() {
      let newTasks = [];
      let formFieldValues = doc.formFieldValues;
      if (!doc.pdfBurnVersion) {
        formFieldValues = doc.formFieldValuesForCopy;
      }
      // const assignedTo =
      //   transaction.agentRepresents === "Buyer"
      //     ? "Buyer Agent"
      //     : "Listing Agent";
      getFormTasksFromDb(doc.formId, transaction).then((tasks) => {
        tasks.forEach((task, index) => {
          let timestamp = null;
          if (formFieldValues && formFieldValues[task.pdfFieldName]) {
            timestamp = formFieldValues[task.pdfFieldName].toDate();
          } else if (formFieldValues && formFieldValues[task.pdfFieldName]) {
            timestamp = formFieldValues[task.pdfFieldName].toDate();
          }
          if (timestamp) {
            const newTask = {
              uniqueId: index,
              end: timestamp,
              title: task.title,
              assignedTo: [],
              docId: doc.id,
              docTitle: doc.title,
              category: "Deadline",
              transactionId: transaction.id,
              transactionTitle: transaction.title,
              status: "Active",
              active: true,
            };
            newTasks = [...newTasks, newTask];
          }
        });
        newTasks = _.sortBy(newTasks, "end");
        newTasks.forEach((task, index) => {
          task.uniqueId = index;
        });

        // Surface the contract-level fields from the form's field values
        const info = {};
        if (formFieldValues) {
          if (doc.documentType === "Offer Agreement" && formFieldValues["Date"]) {
            const rawDate = formFieldValues["Date"];
            const dateObj = rawDate?.toDate
              ? rawDate.toDate()
              : rawDate instanceof Date
              ? rawDate
              : parseDateSafe(rawDate);
            if (dateObj) info.contractDate = dateObj;
          }
          if (formFieldValues["DateClosing Date"]) {
            const rawClosing = formFieldValues["DateClosing Date"];
            const closingObj = rawClosing?.toDate
              ? rawClosing.toDate()
              : rawClosing instanceof Date
              ? rawClosing
              : parseDateSafe(rawClosing);
            if (closingObj) info.closingDate = closingObj;
          }
          const salesPrice = formatCurrencyDisplay(
            formFieldValues["Amount Purchase Price"]
          );
          if (salesPrice) info.salesPrice = salesPrice;
          const earnestMoney = formatCurrencyDisplay(
            formFieldValues["Amount Earnest Money"]
          );
          if (earnestMoney) info.earnestMoney = earnestMoney;
          const sellerConcession = formatCurrencyDisplay(
            formFieldValues["Amount Seller Concession"]
          );
          if (sellerConcession) info.sellerConcession = sellerConcession;
        }
        if (Object.keys(info).length > 0) setContractInfo(info);

        setTasks(newTasks);
        setExtractingDeadlines(false);
      });
    }
    if (doc.formId) {
      extractDeadlinesFromUploadedForm();
    } else {
      extractDeadlinesFromUploadedPdf();
    }
  }, [doc, currentUserProfile.id, transaction]);

  function handleToggleTask(taskUniqueId) {
    const index = tasks.findIndex((task) => task.uniqueId === taskUniqueId);
    let taskToUpdate = tasks[index];
    taskToUpdate["active"] = !taskToUpdate["active"];
    setTasks([
      ...tasks.filter((task) => task.uniqueId !== taskUniqueId),
      taskToUpdate,
    ]);
  }

  function changeTaskDate(date, taskUniqueId) {
    const index = tasks.findIndex((task) => task.uniqueId === taskUniqueId);
    let taskToUpdate = tasks[index];
    taskToUpdate["end"] = date;
    setTasks([
      ...tasks.filter((task) => task.uniqueId !== taskUniqueId),
      taskToUpdate,
    ]);
  }

  function updateContractField(key, value) {
    setContractInfo((prev) => ({ ...(prev || {}), [key]: value }));
  }

  function removeContractField(key) {
    setContractInfo((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleDone() {
    const activeTasks = tasks.filter((task) => task.active === true);

    // Build sharing fields from the selected parties
    let sharingFields = {};
    if (selectedPartyIds.size > 0) {
      const selectedParties = partiesAvailToShare.filter((p) =>
        selectedPartyIds.has(p.id)
      );
      let sharingWithRole = {};
      let sharingWithEmails = new Set();
      selectedParties.forEach((party) => {
        if (!party.email) return;
        const partyRoleCamel = _.camelCase(party.role);
        sharingWithRole[partyRoleCamel] = party.email;
        sharingWithEmails.add(party.email);
        if (
          partyIsAgent(party.role) &&
          party.hasTransactionCoordinator &&
          party.transactionCoordinator?.email
        ) {
          sharingWithRole.otherTransactionCoordinator =
            party.transactionCoordinator.email;
          sharingWithEmails.add(party.transactionCoordinator.email);
        }
      });
      if (Object.keys(sharingWithRole).length > 0) {
        sharingFields = {
          sharingWithRole,
          sharingWith: [...sharingWithEmails],
          mlsNumbers: transaction.mlsNumbers || [],
        };
      }
    }

    const tasksToAdd = activeTasks.map((task) => ({ ...task, ...sharingFields }));

    // Save any remaining edited contract details back to the transaction
    const transactionFields = {};
    if (contractInfo) {
      const salesPrice = stripCurrency(contractInfo.salesPrice);
      if (salesPrice) transactionFields.salesPrice = salesPrice;
      if (contractInfo.contractDate instanceof Date) {
        transactionFields.contractDateTime = contractInfo.contractDate;
      }
      if (contractInfo.closingDate instanceof Date) {
        transactionFields.closingDateTime = contractInfo.closingDate;
      }
      const earnestMoney = stripCurrency(contractInfo.earnestMoney);
      if (earnestMoney) transactionFields.earnestMoneyAmount = earnestMoney;
      const sellerConcession = stripCurrency(contractInfo.sellerConcession);
      if (sellerConcession) transactionFields.sellerConcession = sellerConcession;
    }
    if (Object.keys(transactionFields).length > 0) {
      try {
        await updateTransFieldsInDb(transaction.id, transactionFields);
      } catch (error) {
        toast.error(error.message);
      }
    }

    if (tasksToAdd.length > 0) {
      try {
        await addTasksFromDocToDb(tasksToAdd, tasksTransWithDateAll, transaction);
        toast.success("Deadlines successfully added");
        dispatch(closeModal({ modalType: "DocExtractDeadlinesFromPdf" }));
      } catch (error) {
        toast.error(error.message);
        throw error;
      }
    } else {
      dispatch(closeModal({ modalType: "DocExtractDeadlinesFromPdf" }));
    }
  }

  const ExtractedDateInput = forwardRef(({ value, onClick }, ref) => (
    <Button primary onClick={onClick} ref={ref} icon labelPosition="right">
      {value}
      <Icon name="edit" />
    </Button>
  ));

  if (extractingDeadlines) {
    return <LoadingComponent content="Extracting deadlines from PDF..." />;
  }

  return (
    <ModalWrapper style={{ backgroundColor: "#f9fafb" }}>
      <Segment clearing style={{ backgroundColor: "#f9fafb" }}>
        <Header color="blue" size="large">
          {tasks?.length > 0
            ? "Add Deadlines to Transaction"
            : "No Deadlines Found"}
        </Header>
        <Divider />{" "}
        {tasks && tasks.length > 0 ? (
          <>
            <span>
              You can edit the deadline dates and remove any unwanted deadlines.
              Deadlines will change existing deadlines automatically. Check the
              "Deadlines" section to share with parties.
              <br />
              <br />
              <span className="text blue">
                Note: If you are extracting from an uploaded document, be sure
                to check and verify all extracted dates.
              </span>
            </span>{" "}

            {contractInfo && (
              <Segment
                secondary
                style={{ marginTop: "12px", marginBottom: "4px" }}
              >
                <Header as="h5" color="blue" style={{ marginBottom: "2px" }}>
                  {doc.formId
                    ? "Contract Details"
                    : "Contract Details (AI-Extracted)"}
                </Header>
                <p
                  className="mini bottom margin"
                  style={{ color: "#555", fontSize: "0.85em" }}
                >
                  Edit or remove any of these. Remaining values will be saved to
                  the transaction when you click Add/Change Deadlines.
                </p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  {contractInfo.propertyAddress && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          color: "#555",
                          fontWeight: 600,
                          minWidth: "130px",
                        }}
                      >
                        Property:
                      </span>
                      <span>{contractInfo.propertyAddress}</span>
                    </div>
                  )}
                  {contractInfo.contractDate !== undefined && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          color: "#555",
                          fontWeight: 600,
                          minWidth: "130px",
                        }}
                      >
                        Contract Date:
                      </span>
                      <DatePicker
                        selected={
                          contractInfo.contractDate instanceof Date
                            ? contractInfo.contractDate
                            : null
                        }
                        onChange={(date) =>
                          updateContractField("contractDate", date)
                        }
                        customInput={<ExtractedDateInput />}
                        dateFormat="MMMM d, yyyy"
                      />
                      <Icon
                        link
                        name="delete"
                        color="grey"
                        onClick={() => removeContractField("contractDate")}
                      />
                    </div>
                  )}
                  {contractInfo.closingDate !== undefined && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          color: "#555",
                          fontWeight: 600,
                          minWidth: "130px",
                        }}
                      >
                        Closing Date:
                      </span>
                      <DatePicker
                        selected={
                          contractInfo.closingDate instanceof Date
                            ? contractInfo.closingDate
                            : null
                        }
                        onChange={(date) =>
                          updateContractField("closingDate", date)
                        }
                        customInput={<ExtractedDateInput />}
                        dateFormat="MMMM d, yyyy"
                      />
                      <Icon
                        link
                        name="delete"
                        color="grey"
                        onClick={() => removeContractField("closingDate")}
                      />
                    </div>
                  )}
                  {[
                    { key: "salesPrice", label: "Sales Price" },
                    { key: "earnestMoney", label: "Earnest Money" },
                    { key: "sellerConcession", label: "Seller Concession" },
                  ].map(({ key, label }) =>
                    contractInfo[key] !== undefined ? (
                      <div
                        key={key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            color: "#555",
                            fontWeight: 600,
                            minWidth: "130px",
                          }}
                        >
                          {label}:
                        </span>
                        <Input
                          size="small"
                          value={contractInfo[key] || ""}
                          onChange={(e) =>
                            updateContractField(key, e.target.value)
                          }
                        />
                        <Icon
                          link
                          name="delete"
                          color="grey"
                          onClick={() => removeContractField(key)}
                        />
                      </div>
                    ) : null
                  )}
                </div>
              </Segment>
            )}

            {viewMode === "table" && (
              <Table compact>
                <Table.Header className="mobile hidden">
                  <Table.Row className="small-header-grey">
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>
                      Date {"  "}
                      <Popup
                        flowing
                        size="large"
                        trigger={
                          <Icon
                            name="info"
                            color="blue"
                            circular
                            inverted
                            size="small"
                            style={{ marginLeft: "3px", marginBottom: "3px" }}
                          />
                        }
                      >
                        <p className="bold text blue mini bottom margin">
                          Date Extraction
                        </p>
                        {tasks?.length !== 0 && (
                          <p className="text-dark text-normal mini bottom margin">
                            You can select on the date below to change it.
                          </p>
                        )}
                      </Popup>
                    </Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {tasks?.length !== 0 &&
                    _.sortBy(tasks, "uniqueId").map((task) => (
                      <Table.Row key={task.uniqueId}>
                        <Table.Cell>{task.title}</Table.Cell>
                        <Table.Cell>
                          <DatePicker
                            showIcon
                            selected={task.end}
                            onChange={(date) =>
                              changeTaskDate(date, task.uniqueId)
                            }
                            customInput={<ExtractedDateInput />}
                            dateFormat="MMMM d, yyyy"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            floated={isMobile ? null : "right"}
                            color={task.active ? "grey" : "blue"}
                            onClick={() => handleToggleTask(task.uniqueId)}
                            content={task.active ? "Remove" : "Add"}
                            style={{ cursor: "pointer" }}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table>
            )}

            {viewMode === "calendar" && (
              <div>
                <p className="small bottom margin">
                  Deadlines are shown on the calendar below. You can drag and
                  drop a deadline to a new date to change it.
                  <br />
                  <i>
                    Mouse hover on a deadline to see the full deadline name.
                  </i>
                </p>
                <DnDCalendar
                  localizer={calendarLocalizer}
                  events={
                    tasks
                      ?.filter((task) => task.active && task.end)
                      .map((task) => ({
                        id: task.uniqueId,
                        title: task.title,
                        start: task.end,
                        end: task.end,
                        allDay: true,
                      })) || []
                  }
                  startAccessor="start"
                  endAccessor="end"
                  views={["month"]}
                  popup
                  defaultDate={tasks?.find((t) => t.end)?.end || new Date()}
                  style={{
                    height: 600,
                    marginTop: "8px",
                    width: "100%",
                  }}
                  onEventDrop={({ event, start }) => {
                    if (!event || !start) return;
                    changeTaskDate(new Date(start), event.id);
                  }}
                  eventPropGetter={() => ({
                    style: { backgroundColor: "#57b2ff", color: "white" },
                  })}
                />
              </div>
            )}

            {partiesAvailToShare.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <Divider />
                <Header as="h4" color="blue" style={{ marginBottom: "4px" }}>
                  Auto-Share Deadlines
                </Header>
                <p style={{ marginBottom: "10px", color: "#555" }}>
                  Select to automatically share all of these deadlines with each
                  of the following parties, or manually share them from the{" "}
                  <strong>Tasks</strong> section of this transaction.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px 24px",
                  }}
                >
                  {partiesAvailToShare.map((party) => {
                    const name = convertFullName(party).trim();
                    const roleDisplay = convertRoleToSharingDisplay(party.role);
                    const label = name
                      ? `${roleDisplay} — ${name}`
                      : roleDisplay;
                    return (
                      <Checkbox
                        key={party.id}
                        label={label}
                        checked={selectedPartyIds.has(party.id)}
                        onChange={(_e, { checked }) =>
                          handlePartyCheckboxChange(party.id, checked)
                        }
                      />
                    );
                  })}
                </div>
              </div>
            )}

            <Divider className="medium top margin" />
            <Button
              primary
              floated={isMobile ? null : "right"}
              content="Add/Change Deadlines"
              onClick={() => handleDone()}
              className={isMobile ? "fluid medium bottom margin" : null}
            />
            <Button
              floated={isMobile ? null : "right"}
              content={viewMode === "table" ? "View as Calendar" : "View as Table"}
              onClick={() =>
                setViewMode(viewMode === "table" ? "calendar" : "table")
              }
              type="button"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
            <Button
              floated={isMobile ? null : "left"}
              content="Cancel"
              onClick={() =>
                dispatch(
                  closeModal({ modalType: "DocExtractDeadlinesFromPdf" })
                )
              }
              type="button"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
          </>
        ) : (
          <>
            <p>There are no deadlines to extract in this document or the data is unable to be extracted.
              <br/><i>You may need to manually add the deadlines for this document.</i>
            </p>
            <Divider className="medium top margin" />
            <Button
              content="Cancel"
              onClick={() =>
                dispatch(
                  closeModal({ modalType: "DocExtractDeadlinesFromPdf" })
                )
              }
              type="button"
              className={isMobile ? "fluid" : null}
            />
          </>
        )}
      </Segment>
    </ModalWrapper>
  );
}
